import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import FrontText from "../FrontText";
import FrontDotsDecoration from "../Partials/FrontDotsDecoration";
import FrontPrimaryButton from "../Buttons/FrontPrimaryButton";
import { Trans, useTranslation } from 'react-i18next';
import FrontSearcherFilters from "./FrontSearcherFilters";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { router, useForm } from "@inertiajs/react";
import resolveLocaleRoute from "@/Helpers/resolveLocaleRoute";
import DOMPurify from "dompurify";
import throttle from "@/Helpers/throttle";

const FrontStandardSearcher = ({
    smallHeading, 
    heading,
    countWarehouses,
    cities,
    warehouses,
    regions,
    filterRoute,
    queryParams,
    currentProvince = null,
    currentCity = null,
    searchRegion = null
}) => {
    
    let inputClasses =
        "bg-white placeholder:text-gray-text text-standard-black font-medium italic text-14 md:pl-c15 pl-2 md:pr-5 pr-2 md:py-c17 py-c11 leading-c17 rounded-r-12  focus:outline-none focus:ring-0 border-transparent border-2 border-l-0 focus:border-yellow transition-all ease-in-out duration-300 max-w-full md:h-14 h-11";

    const { data, setData } = useForm({
        provinces: [],
        cities: [],
        areaFrom: null,
        areaTo: null,
    });

    const submit = (e) => {
        e.preventDefault();

        if(data.provinces.length == 0 && data.cities.length == 0 && (data.keyword == undefined || data.keyword.length == 0)){
            
            if (i18n.resolvedLanguage === 'pl') {
                return router.visit(route('front.warehouses.all'), {
                    data: {
                        ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                        ...(data.areaTo ? {areaTo: data.areaTo} : null),
                    },
                })
            } else {
                return router.visit(route('front.warehouses.all.en'), {
                    data: {
                        ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                        ...(data.areaTo ? {areaTo: data.areaTo} : null),
                    }
                })
            }
        }
        if ((data.provinces.length >= 1 && data.cities.length >= 1) || data.provinces.length > 1 || data.cities.length > 1) {
            // front.warehouses.all.manyLocations
            if (i18n.resolvedLanguage === 'pl') {
                router.visit(route('front.warehouses.manyLocations'), {
                    data: {
                        provinces: '[' + data.provinces.join(',') + ']',
                        cities: '[' + data.cities.join(',') + ']',
                        ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                        ...(data.areaTo ? {areaTo: data.areaTo} : null),
                    },
                })
            } else {
                router.visit(route('front.warehouses.manyLocations.en'), {
                    data: {
                        provinces: '[' + data.provinces.join(',') + ']',
                        cities: '[' + data.cities.join(',') + ']',
                        ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                        ...(data.areaTo ? {areaTo: data.areaTo} : null),
                    }
                })
            }
        } else if (data.provinces.length === 0 && data.cities.length === 0) {
            // results by keywords, search in warehouse name and landlord name
            if (i18n.resolvedLanguage === 'pl') {
                router.visit(route('front.warehouses', { 
                    searchRegion: data.keyword,
                    ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                    ...(data.areaTo ? {areaTo: data.areaTo} : null),
                }))
            } else {
                router.visit(route('front.warehouses.en', { 
                    searchRegion: data.keyword,
                    ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                    ...(data.areaTo ? {areaTo: data.areaTo} : null),
                }))
            }
        } else {
            // single results page
            let regionToFind;

            if (data.provinces[0]) {
                regionToFind = data.provinces[0]
            }

            if (data.cities[0]) {
                regionToFind = data.cities[0]
            }

            if (i18n.resolvedLanguage === 'pl') {
                router.visit(route('front.warehouses', { 
                    searchRegion: regionToFind,
                    ...(data.areaFrom ? {areaFrom: data.areaFrom} : null),
                    ...(data.areaTo ? {areaTo: data.areaTo} : null),
                }))
            } else {
                router.visit(route('front.warehouses.en', { 
                    searchRegion: regionToFind
                }))
            }
        }
    }

    const { t, i18n } = useTranslation();

    const [isFiltersVisible, setIsFiltersVisible] = useState(false);
    const [stateRegions, setStateRegions] = useState(regions);

    function outsideClick(ref) {
        useEffect(() => {
            /**
             * Hide if clicked on outside of element
             */
            function handleClickOutside(event) {
                if (ref.current && !ref.current.contains(event.target)) {
                    setIsFiltersVisible(false);
                }
            }
            // Bind the event listener
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                // Unbind the event listener on clean up
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref]);
    }

    const wrapperRef = useRef(null);
    outsideClick(wrapperRef);

    const handleSearch = (e) => {
        const keyword = e.currentTarget.value;
        setData('keyword', keyword);
        let destinationRoute = '';

        if (i18n.resolvedLanguage === 'pl') {
            destinationRoute = filterRoute;
        } else {
            destinationRoute = filterRoute + '.en'
        }
        let routeDataObject = {
            keyword: keyword,
            ...(queryParams ? queryParams : {}),
        };

        let destinationSearchRegion = searchRegion;
        const provinceOrCity = i18n.resolvedLanguage === 'pl'
            ? (currentProvince != null ? currentProvince.slug : currentCity)
            : (currentProvince != null ? currentProvince.name_en : currentCity);

        if (provinceOrCity !== null) {
            destinationSearchRegion = provinceOrCity;
        }

        if(destinationSearchRegion !== null && (destinationRoute === 'front.warehouses' || destinationRoute === 'front.warehouses.en')) {
            routeDataObject['searchRegion'] = destinationSearchRegion;
        }

        router.visit(route(destinationRoute, routeDataObject), {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['regions', 'cities', 'warehouses'],
            onSuccess: (data) => {
                setStateRegions(data.props.regions)
            }
        })
    }

    const throttledHandleSearch = useMemo(() => throttle(handleSearch, 50), []);
    
    return (
        <div>
            <Container className="relative">
                <Row className='justify-center'>
                    <Column xl={11}>
                        <form onSubmit={submit} className="bg-dark-blue-standard rounded-20 xl:px-16 lg:px-10 px-5 xl:pb-12 lg:pb-10 pb-5 pt-5 flex lg:gap-5 gap-y-5 items-end flex-wrap lg:flex-nowrap">
                            <div className="w-full" ref={wrapperRef}>
                                <label htmlFor="city">
                                    <FrontText
                                        textSize="14/17"
                                        className="text-white font-bold mb-2"
                                    >
                                        {t('search_form.location')}
                                    </FrontText>
                                </label>

                                <div className="relative h-full md:pl-14 pl-11">
                                    <div className="absolute left-0 h-full md:w-14 w-11 bg-yellow rounded-l-12 flex items-center justify-center">
                                        <img
                                            src="/storage/icons/localisation-icon.svg"
                                            width={18}
                                            height={26}
                                            alt=""
                                            className="md:w-auto md:h-auto w-[13px] h-[19px]"
                                        />
                                    </div>
                                    <input
                                        className={`${inputClasses} w-full`}
                                        id="city"
                                        name="city"
                                        type="text"
                                        autoComplete="off"
                                        onFocus={() => setIsFiltersVisible(true)}
                                        onChange={throttledHandleSearch}
                                        placeholder={t('search_form.location_placeholder')}
                                    />
                                    <div className="relative">
                                        <FrontSearcherFilters
                                            regions={stateRegions}
                                            cities={cities}
                                            warehouses={warehouses}
                                            visible={isFiltersVisible}
                                            setSelected={(selectedProvinces, selectedCities) => {
                                                setData({
                                                    provinces: selectedProvinces,
                                                    cities: selectedCities,
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="hidden lg:block xl:max-w-50 lg:max-w-c150 max-w-[50%] w-full lg:pr-0 pr-2">
                                <label htmlFor="area_from">
                                    <FrontText
                                        textSize="14/17"
                                        className="text-white font-bold mb-2"
                                    >
                                        <Trans i18nKey={'search_form.area_from'} />
                                    </FrontText>
                                </label>

                                <div className="relative h-full md:pl-14 pl-11">
                                    <div className="absolute left-0 h-full md:w-14 w-11 bg-yellow rounded-l-12 flex items-center justify-center">
                                        <img
                                            src="/storage/icons/icon-m2-black.svg"
                                            width={34}
                                            height={34}
                                            alt=""
                                            className="md:w-c34 md:h-c34 h-6 w-6"
                                        />
                                    </div>
                                    <input
                                        className={`${inputClasses} w-full`}
                                        id="area_from"
                                        name="area_from"
                                        type="number"
                                        onChange={(e) => setData('areaFrom', e.target.value)}
                                        defaultValue={queryParams ? queryParams.areaFrom : ''}
                                        placeholder={t('search_form.area_from_placeholder')}
                                    />
                                </div>
                            </div>

                            <div className="hidden lg:block xl:max-w-50 lg:max-w-c150 max-w-[50%] w-full lg:pl-0 pl-2">
                                <label htmlFor="area_to">
                                    <FrontText
                                        textSize="14/17"
                                        className="text-white font-bold mb-2"
                                    >
                                        <Trans i18nKey={'search_form.area_to'} />
                                    </FrontText>
                                </label>

                                <div className="relative h-full md:pl-14 pl-11">
                                    <div className="absolute left-0 h-full md:w-14 w-11 bg-yellow rounded-l-12 flex items-center justify-center">
                                        <img
                                            src="/storage/icons/icon-m2-black.svg"
                                            width={34}
                                            height={34}
                                            alt=""
                                            className="md:w-c34 md:h-c34 h-6 w-6"
                                        />
                                    </div>
                                    <input
                                        className={`${inputClasses} w-full`}
                                        id="area_to"
                                        name="area_to"
                                        type="number"
                                        onChange={(e) => setData('areaTo', e.target.value)}
                                        defaultValue={queryParams ? queryParams.areaTo : ''}
                                        placeholder={t('search_form.area_to_placeholder')}
                                    />
                                </div>
                            </div>

                            <FrontPrimaryButton displayAsButton type="submit" className="lg:w-auto w-full">
                                {t('search_form.search')}
                            </FrontPrimaryButton>
                        </form>

                        <FrontText textSize='14/17' className="font-medium mt-4 mb-10">
                            <p>
                                {t('search_form.counted_warehouses')} <span className="font-semibold">{countWarehouses}</span>
                            </p>
                        </FrontText>
                    </Column>
                </Row>
            </Container>
        </div>
    );
};

export default FrontStandardSearcher;
